import {FC, Fragment} from "react";
import styled from "@emotion/styled";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {getFanVoteRankings} from "modules/selectors";
import {breakpoints} from "assets/theme";

const Wrapper = styled.div`
	max-width: 1440px;
	width: 100%;
	margin: 40px auto 0;
	padding: 0;

	@media (min-width: ${breakpoints.tablet}) {
		padding: 0 60px;
	}
`;

const Container = styled.div`
	border-radius: 4px;
	background: #545456;
	border: 2px solid ${({theme}) => theme.colorPalette.secondary};
	font-family: ${({theme}) => theme.baseFont};
	color: ${({theme}) => theme.colorPalette.white};
`;

const Title = styled.div`
	text-align: center;
	font-size: 20px;
	font-weight: 700;
	line-height: 120%;
	letter-spacing: 0.2px;
	text-transform: uppercase;
	padding: 20px 10px 10px;

	@media (min-width: ${breakpoints.tablet}) {
		font-size: 32px;
	}
`;

const Table = styled.div`
	display: grid;
	grid-template-columns: 68px 1fr 83px;
	max-height: 600px;
	overflow: auto;
`;

export const TableItem = styled.div`
	min-height: 48px;
	padding: 10px 12px;
	font-family: "Hind", sans-serif;
	font-weight: 500;
	font-size: 14px;
	line-height: 120%;
	color: ${({theme}) => theme.colorPalette.white};
	background: #545456;
	border-bottom: 1px solid ${({theme}) => theme.colorPalette.white};

	&.first-column {
		padding: 10px 12px 10px 20px;
	}

	&.last-column {
		padding: 10px 20px 10px 12px;
	}

	&.flex-start {
		display: flex;
		justify-content: flex-start;
		align-items: center;
		text-align: center;
	}

	&.center {
		display: flex;
		align-items: center;
		justify-content: center;
		text-align: center;
	}

	&.divider {
		position: relative;

		&:after {
			content: "";
			position: absolute;
			width: 1px;
			right: 0;
			height: 28px;
			background: #cccccc;
		}
	}

	&.sticky {
		position: sticky;
		top: 0;
		z-index: 1;
	}
`;

export const TableHead = styled(TableItem)`
	background: ${({theme}) => theme.colorPalette.primary};
	color: ${({theme}) => theme.colorPalette.black};
	font-weight: 700;

	&.divider:after {
		background: ${({theme}) => theme.colorPalette.black};
	}
`;

const PlayerName = styled.div`
	font-size: 16px;
	font-weight: 700;
	line-height: 20px;
	letter-spacing: 0.2px;
	font-family: ${({theme}) => theme.baseFont};
	text-align: start;
`;

export const Leaderboard: FC = () => {
	const {t} = useTranslation();
	const rankings = useSelector(getFanVoteRankings);

	return (
		<Wrapper>
			<Container>
				<Title>{t("page.vote.leaderboard.title")}</Title>

				<Table>
					<TableHead className="first-column center divider sticky">
						{t("page.vote.leaderboard.rank")}
					</TableHead>
					<TableHead className="flex-start sticky">
						{t("page.vote.leaderboard.player_name")}
					</TableHead>
					<TableHead className="last-column center sticky">
						{t("page.vote.leaderboard.votes")}
					</TableHead>

					{rankings.map((ranking, i) => (
						<Fragment key={`${ranking.rank}-${i}`}>
							<TableItem className="first-column flex-start divider">
								{ranking.rank}
							</TableItem>
							<TableItem className="flex-start">
								<PlayerName>
									{`${ranking.firstName} ${ranking.lastName}`}
								</PlayerName>
							</TableItem>
							<TableItem className="last-column center">
								{Math.round(ranking.percent)}%
							</TableItem>
						</Fragment>
					))}
				</Table>
			</Container>
		</Wrapper>
	);
};
